import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import {
    RiskGapNotes,
    RiskGapAuditNotes,
    ModalTabs,
    DetailAttestationInfo,
    HistoricDocuments,
} from '~/components/Patient';
import { enums, gapIsFailed, gapIsPending, handleButtonKeypress } from '~/util';
import { usePatientService } from '~/services';
import { BSModal } from '~/components/Bootstrap';
import { RiskGapReminders } from '~/components/Reminders';

interface DetailAttestationProps {
    riskGap: RiskGapType;
    patientId: number;
    className?: string | undefined;
    children?: React.ReactNode;
}

export function DetailAttestation({
    riskGap,
    patientId,
    className = '',
    children,
}: DetailAttestationProps) {
    const { getGap, riskGapResponse } = usePatientService();
    const [answer, setAnswer] = React.useState(riskGap.uiAnswer);
    const [show, setShow] = React.useState(false);
    const [tab, setTab] = React.useState('Info');
    const [loading, setLoading] = React.useState(false);
    const [addReminderOpen, setAddReminderOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        setAnswer(riskGap.uiAnswer);
    }, [riskGap.uiAnswer]);

    async function handleShow(
        e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
    ) {
        // Prevent showing modal when clicking within separate click zones and modals
        const targets = [
            '.answer-container',
            '.attestation-modal-card',
            '.bookmark-document',
            '.bookmark-document-modal',
        ];
        if ((e.target as HTMLElement).closest(targets.join(', '))) {
            e.stopPropagation();
            return false;
        }
        if ((e.target as HTMLElement).closest('.reminder-icon')) {
            if (riskGap?.tasks?.length === 0) {
                setAddReminderOpen(true);
            }
            setTab('Reminders');
        }
        setLoading(true);
        setShow(true);

        // Load HCC data (parent component should handle gap_loaded event
        // and the prop should be updated down the chain)
        try {
            await getGap(riskGap.id, riskGap.type);
        } catch (error) {
            // Empty catch
        } finally {
            setLoading(false);
        }
        return null;
    }

    function handleClose() {
        setShow(false);
        setAnswer(riskGap.uiAnswer);
        setAddReminderOpen(false);
    }

    async function handleSave() {
        if (answer === null || answer === undefined) return;
        try {
            setSaving(true);
            await riskGapResponse(riskGap.id, answer, riskGap.updated_at ?? null, {
                handledErrors: { 400: false },
            });
        } catch (error) {
            // Empty catch
        } finally {
            setSaving(false);
            setShow(false);
            setAddReminderOpen(false);
        }
    }

    const hasSave = (gapIsPending(riskGap) || gapIsFailed(riskGap)) && riskGap.uiAnswer !== answer;

    const tabs = ['Info', 'Reminders', 'Notes', 'History'];

    if (!riskGap.is_added) tabs.push('Documents');

    if (!riskGap?.id) return null;

    return (
        <>
            <div
                className={className}
                onClick={handleShow}
                onKeyUp={(e) => handleButtonKeypress(e, handleShow)}
                role="button"
                tabIndex={0}
            >
                {children}
            </div>
            <BSModal
                show={show}
                onHide={handleClose}
                className="attestation-modal-card gap-modal"
                aria-labelledby={`pcm-att-${riskGap.id}`}
            >
                <Modal.Header closeButton>
                    <Modal.Title id={`pcm-att-${riskGap.id}`} role="heading" aria-level={1}>
                        <div>
                            {riskGap.is_added ? riskGap.code : riskGap!.icd!.code}{' '}
                            {enums(['gap', 'status', riskGap.status])}
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ModalTabs tab={tab} setTab={setTab} tabs={tabs} />
                    {loading && tab === 'Info' && (
                        <div className="text-center">
                            <Spinner animation="border" />
                        </div>
                    )}
                    {tab === 'Info' && !loading && (
                        <DetailAttestationInfo
                            riskGap={riskGap}
                            patientId={patientId}
                            answer={answer}
                            handleClose={handleClose}
                            handleInputChange={(e: {
                                target: {
                                    value: boolean;
                                };
                            }) => setAnswer(e.target.value)}
                        />
                    )}
                    {tab === 'Reminders' && (
                        <RiskGapReminders
                            riskGapId={riskGap.id}
                            patientId={patientId}
                            addReminderOpen={addReminderOpen}
                        />
                    )}
                    {tab === 'Notes' && (
                        <RiskGapNotes entityId={riskGap.id} patientId={patientId} />
                    )}
                    {tab === 'History' && <RiskGapAuditNotes entityId={riskGap.id} gap={riskGap} />}
                    {tab === 'Documents' && <HistoricDocuments icdCode={riskGap!.icd!.code} />}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" type="button" onClick={handleClose}>
                        {hasSave ? 'Cancel' : 'Close'}
                    </button>
                    {hasSave && (
                        <button
                            id="risk-gap-save"
                            className="btn btn-primary"
                            type="button"
                            disabled={saving}
                            onClick={handleSave}
                        >
                            {saving && (
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    className="me-2"
                                />
                            )}
                            Save
                        </button>
                    )}
                </Modal.Footer>
            </BSModal>
        </>
    );
}
